
import { Language, LANGUAGE_REGEX_GROUP } from '@/types/language';

// Shared regex instances built from the single source of truth
const langPrefixRe = new RegExp(`^/(${LANGUAGE_REGEX_GROUP})(/|$)`);
const dupLangPrefixRe = new RegExp(`^/(${LANGUAGE_REGEX_GROUP})/(${LANGUAGE_REGEX_GROUP})(/|$)`);

/**
 * Checks if a path has a language prefix
 */
export const hasLanguagePrefix = (path: string): boolean => {
  return langPrefixRe.test(path);
};

/**
 * Gets the language from a path
 */
export const getLanguageFromPath = (path: string): Language | null => {
  const match = path.match(langPrefixRe);
  return match ? match[1] as Language : null;
};

/**
 * Removes the language prefix from a path
 */
export const removeLanguagePrefix = (path: string): string => {
  return path.replace(langPrefixRe, '/');
};

/**
 * Normalizes a path to avoid duplicate language prefixes
 */
export const normalizePath = (path: string): string => {
  if (!path) return '/';
  
  const match = path.match(dupLangPrefixRe);
  
  if (match) {
    const lang = match[2];
    const restOfPath = path.replace(dupLangPrefixRe, '');
    return `/${lang}${restOfPath}`;
  }
  
  return path;
};

/**
 * Gets the localized path for a given language
 * Preserves search parameters if provided
 */
/**
 * Checks if a path is the reset-password route, with or without a language prefix.
 * Uses removeLanguagePrefix (backed by LANGUAGE_REGEX_GROUP) so it automatically
 * supports every language in the single source of truth.
 */
export const isResetPasswordPath = (path: string): boolean => {
  const stripped = removeLanguagePrefix(path);
  return stripped === '/reset-password';
};

export const getLocalizedPath = (path: string, language: Language, preserveSearch: string = ''): string => {
  let cleanPath = path;
  let searchParams = preserveSearch;
  
  if (path.includes('?')) {
    const [pathOnly, search] = path.split('?');
    cleanPath = pathOnly;
    searchParams = searchParams || `?${search}`;
  }
  
  cleanPath = hasLanguagePrefix(cleanPath) ? removeLanguagePrefix(cleanPath) : cleanPath;
  
  if (cleanPath === '/' && language === 'en') {
    const result = searchParams ? `/${searchParams}` : '/';
    return result;
  }
  
  let localizedPath = language === 'en' ? 
    cleanPath : 
    `/${language}${cleanPath === '/' ? '' : cleanPath}`;
  
  if (searchParams) {
    localizedPath = `${localizedPath}${searchParams}`;
  }
  
  return localizedPath;
};
